import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { Plus } from "lucide-react";
import Breadcrumbs from "components/common/Breadcrumbs";
import useIsMobile from "components/hooks/useIsMobile";
import {
  useAdminListDelegationTasks,
  useAdminUpdateDelegationTask,
} from "services/delegation";
import Loading from "components/common/Loading";
import DelegationCard from "components/delegation/DelegationCard";
import CreateLightningTaskForm from "components/delegation/CreateLightningTaskForm";

type StatusFilter = "pending" | "active" | "done";

const PENDING_STATUSES = ["pending", "queued"];
const ACTIVE_STATUSES = ["in_progress", "delegated"];
const DONE_STATUSES = ["completed", "cancelled"];

export default () => {
  const isMobile = useIsMobile();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [createOpen, setCreateOpen] = useState(false);
  const { request, data, loading } = useAdminListDelegationTasks();
  const { request: updateTask } = useAdminUpdateDelegationTask(() => {
    refresh();
  });

  useEffect(() => {
    refresh();
  }, [statusFilter]);

  const refresh = async () => {
    const statuses =
      statusFilter === "pending"
        ? PENDING_STATUSES
        : statusFilter === "active"
          ? ACTIVE_STATUSES
          : DONE_STATUSES;
    await request({ statuses });
  };

  const handleStatusChange = async (ref: string, newStatus: string) => {
    await updateTask({
      ref,
      status: newStatus,
      timeSpentMinutes: 0,
      commentContent: "",
      commentContentHtml: "",
      attachments: [],
      recipientType: "",
    });
  };

  const tasks = data?.delegationTasks || [];

  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px 20px 0px 20px" : "64px 64px 0 64px",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Breadcrumbs
            breadcrumbs={[
              { name: "Home", link: "/" },
              { name: "Tools", link: "/tools" },
            ]}
          />
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Box
              display="flex"
              flexDirection="row"
              gap="12px"
              alignContent="center"
            >
              <Typography variant="display">Delegations</Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => setCreateOpen(true)}
              sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}
            >
              Create lightning task
            </Button>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create lightning task</DialogTitle>
        <DialogContent>
          <CreateLightningTaskForm
            onCancel={() => setCreateOpen(false)}
            onCreated={() => {
              setCreateOpen(false);
              refresh();
            }}
          />
        </DialogContent>
      </Dialog>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          gap: isMobile ? "12px" : "24px",
          marginBottom: "16px",
        }}
      >
        <ButtonGroup
          style={{
            display: "flex",
            ...(isMobile && {
              width: "100%",
            }),
          }}
        >
          <Button
            sx={{ flex: "1", width: "100%" }}
            variant={
              statusFilter === "pending" ? "buttonGroupSelected" : "buttonGroup"
            }
            onClick={() => setStatusFilter("pending")}
          >
            Pending
          </Button>
          <Button
            sx={{ flex: "1", width: "100%" }}
            variant={
              statusFilter === "active" ? "buttonGroupSelected" : "buttonGroup"
            }
            onClick={() => setStatusFilter("active")}
          >
            Active
          </Button>
          <Button
            sx={{ flex: "1", width: "100%" }}
            variant={
              statusFilter === "done" ? "buttonGroupSelected" : "buttonGroup"
            }
            onClick={() => setStatusFilter("done")}
          >
            Done
          </Button>
        </ButtonGroup>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "12px",
          paddingBottom: "16px",
        }}
      >
        {loading && <Loading />}
        {!loading && !tasks.length && (
          <Alert severity="info">No delegation tasks found</Alert>
        )}
        {!loading &&
          tasks.map((task) => (
            <DelegationCard
              key={task.ref}
              task={task}
              onStatusChange={handleStatusChange}
            />
          ))}
      </Box>
    </Box>
  );
};
